import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Splitter, Button, Spin } from 'antd';
import { Link } from 'react-router-dom';
import * as actions from '../../store/actions/PracticeTestActions';
import { usePracticeTestData } from './hooks/usePracticeTestData';
import { usePracticeTestNavigation } from './hooks/usePracticeTestNavigation';
import PracticeTestHeader from './components/PracticeTestHeader';
import PracticeTestStats from './components/PracticeTestStats';
import QuestionSidebar from './components/QuestionSidebar';
import MCQQuestion from './components/QuestionContent/MCQQuestion';
import CodingQuestion from './components/QuestionContent/CodingQuestion';
import SubmissionTimeline from './components/SubmissionSection/SubmissionTimeline';
import CodeEditor from './components/SubmissionSection/CodeEditor';
import TestCaseResults from './components/SubmissionSection/TestCaseResults';
import { useTranslation } from 'react-i18next';
import styles from './PracticeTestResult.scss';

const PracticeTestResult = ({ style }) => {
  const { t } = useTranslation('practice_tests');
  const dispatch = useDispatch();
  const [localActiveSubmission, setLocalActiveSubmission] = useState(null);

  // Custom hooks
  const { assessment, curr_submission, code, curr_question, recordings, test_cases, submissions } =
    usePracticeTestData();

  const handleSubmissionChange = submission => {
    setLocalActiveSubmission(submission);

    if (actions.setActiveSubmission) {
      dispatch(
        actions.setActiveSubmission({
          question_id: curr_question.id,
          submission_id: submission.id,
        }),
      );
    }
  };

  useEffect(() => {
    setLocalActiveSubmission(null);
  }, [curr_question?.id]);

  // Add safety checks for assessment data
  if (!assessment || !assessment.questions || !curr_question) {
    return (
      <div
        data-testid="loading-state"
        data-specific-id="assessment-loading-message"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  let questions_index = Object.keys(assessment.questions);
  let isInProgress = assessment.status === 'PG';

  const allSubmissions = curr_question
    ? submissions?.[curr_question.id]?.code_submissions || []
    : [];

  const activeSubmission = localActiveSubmission || curr_submission;
  const activeCode = localActiveSubmission?.code_to_execute || localActiveSubmission?.code || code;

  return (
    <div
      style={{ ...style }}
      className={styles['c-practice-test-result']}
      data-testid="practice-test-result"
    >
      <PracticeTestHeader assessment={assessment} />

      <PracticeTestStats
        assessment={assessment}
        recordings={recordings}
      />

      <div
        className={styles['c-practice-test-result-body']}
        data-test-id="practice-test-result-body"
        data-specific-id="assessment-main-body"
      >
        <Splitter
          style={{
            boxShadow: 'none',
            border: 'none',
          }}
          disabled={true}
          resizable={false}
          split={false}
          data-test-id="main-splitter"
          data-specific-id="assessment-splitter"
        >
          {/* Left Sidebar Panel - Fixed width */}
          <Splitter.Panel
            size="15%"
            data-test-id="sidebar-panel"
            data-specific-id="sidebar-panel-container"
            style={{
              borderRight: '1px solid #e8e8e8',
              minWidth: '160px',
              maxWidth: '160px',
              marginRight: '30px',
              marginLeft: '20px',
            }}
          >
            <QuestionSidebar
              assessment={assessment}
              questions_index={questions_index}
              activeQuestionId={curr_question.id}
            />
          </Splitter.Panel>

          {/* Main Content Panel - Remaining width */}
          <Splitter.Panel
            resizable={false}
            style={{ overflow: 'hidden' }}
            data-test-id="content-panel"
            data-specific-id="main-content-panel"
          >
            <div
              style={{
                padding: '20px 20px 0 20px',
                background: 'white',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowY: 'auto',
              }}
              data-test-id="content-container"
              data-specific-id="content-container-div"
            >
              {/* Question Header with View Recording Button */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
                data-test-id="question-header"
                data-specific-id="question-header-section"
              >
                <div
                  style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}
                  data-test-id="question-title-label"
                  data-specific-id="question-label"
                >
                  {t('practice_tests.test_result.question')}
                </div>
                {recordings &&
                  recordings[assessment.assessment_id] &&
                  recordings[assessment.assessment_id].length > 0 && (
                    <Button
                      type="primary"
                      style={{
                        height: '40px',
                        padding: '0 24px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                      data-test-id="view-recording-button"
                      data-specific-id={`view-recording-btn-${assessment.assessment_id}`}
                      data-action="view-recording"
                    >
                      <Link
                        to={`/practice/assessment/recordings/${assessment.assessment_id}`}
                        style={{ color: 'white' }}
                        data-test-id="recording-link"
                        data-specific-id="recording-navigate-link"
                      >
                        {t('practice_tests.test_result.view_recording')}
                      </Link>
                    </Button>
                  )}
              </div>

              {/* Render MCQ or Coding Question based on type */}
              {curr_question.type === 'mcq' ? (
                <MCQQuestion
                  curr_question={curr_question}
                  selectedOptions={curr_question.selected_options || []}
                />
              ) : (
                <CodingQuestion curr_question={curr_question} />
              )}

              {/* Submission and Code Editor Section - Only for Coding Questions */}
              {curr_question.type === 'coding' && activeSubmission?.id ? (
                <div
                  style={{ marginTop: '30px' }}
                  data-test-id="submission-section"
                  data-specific-id="coding-submission-section"
                >
                  <SubmissionTimeline
                    curr_submission={activeSubmission}
                    allSubmissions={allSubmissions}
                    onSubmissionChange={handleSubmissionChange}
                    testStartTime={assessment.start_time}
                    testEndTime={assessment.end_time}
                  />

                  {/* Code Editor and Test Cases Side by Side */}
                  <div
                    style={{
                      background: '#F9FAFB',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                    }}
                    data-test-id="editor-results-container"
                    data-specific-id="code-editor-results-wrapper"
                  >
                    <Row
                      gutter={20}
                      data-test-id="editor-results-row"
                      data-specific-id="editor-results-row"
                    >
                      {/* Left: Code Editor */}
                      <Col
                        span={12}
                        data-test-id="code-editor-col"
                        data-specific-id="code-editor-column"
                      >
                        <CodeEditor
                          code={activeCode}
                          curr_submission={activeSubmission}
                        />
                      </Col>

                      <Col
                        span={12}
                        data-test-id="test-results-col"
                        data-specific-id="test-results-column"
                        style={{ height: '500px', overflow: 'auto' }}
                      >
                        <TestCaseResults
                          curr_question={curr_question}
                          curr_submission={activeSubmission}
                          test_cases={test_cases}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              ) : curr_question.type === 'coding' ? (
                <div
                  style={{
                    background: '#F5F5F5',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6B7280',
                    marginTop: '30px',
                  }}
                  data-test-id="no-submissions"
                  data-specific-id="no-submissions-placeholder"
                >
                  {t('practice_tests.test_result.no_submissions')}
                </div>
              ) : null}
            </div>
          </Splitter.Panel>
        </Splitter>
      </div>
      <div className={styles['c-practice-test-result-notes']}></div>
    </div>
  );
};

export default PracticeTestResult;
